import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog as R } from "radix-ui";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { formatDate } from "../lib/format";
import { money } from "../lib/money";
import { canBilling, canCreateSites } from "../lib/roles";
import { useLiveEvents, useLiveState, type WatchHome, type WatchMessage } from "../api/live";
import { useMe } from "../api/queries";
import { setMutationErrorFallback } from "../api/queryClient";
import { useApplyBrand } from "../api/brand";
import type { MeOrg, Operation, WpAdmin } from "../api/types";
import { cx } from "../lib/cx";
import { isTyping, useLocalStorage } from "../lib/hooks";
import { IconAlertCircle, IconRefresh, IconX } from "../ui/icons";
import { PageLayout, pageWidth } from "../ui/PageLayout";
import { useToast } from "../ui/Toast";
import { OrgContext } from "./org";
import { AccountStrip } from "./accounts";
import { navFor, SidebarContent } from "./Sidebar";
import { TopBar } from "./TopBar";
import { GoogleGate } from "../pages/auth/Google";
import { ShellContext, type ShellUI } from "./ui";

/*
 * The overlays are part of the shell but not of its first paint: their
 * chunks load right after the shell renders, so they're ready when opened.
 */
const NewSiteSheet = lazy(() => import("../pages/sites/NewSiteSheet").then((m) => ({ default: m.NewSiteSheet })));
const AddServerDialog = lazy(() => import("../pages/servers/AddServerDialog").then((m) => ({ default: m.AddServerDialog })));
const CommandPalette = lazy(() => import("./CommandPalette").then((m) => ({ default: m.CommandPalette })));
const ShortcutsSheet = lazy(() => import("./ShortcutsSheet").then((m) => ({ default: m.ShortcutsSheet })));

function LiveBanner() {
  const state = useLiveState();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (state === "open") {
      setShow(false);
      return;
    }
    // Brief blips (a reconnect) shouldn't flash a banner.
    const t = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(t);
  }, [state]);
  if (!show) return null;
  return (
    // The whole app's state, not a page's: a slim strip under the top bar, in the notices' gray (it's information).
    <div role="status" className="flex items-center justify-center gap-2 border-b border-line bg-panel px-5 py-2 text-[13px] text-body sm:px-8">
      <IconRefresh size={14} className="shrink-0 animate-spin text-dim [animation-duration:2.5s]" aria-hidden="true" />
      <span>
        <span className="font-semibold text-fg">Reconnecting.</span> Live updates are paused.
      </span>
    </div>
  );
}

/** Milestone 6: an unpaid invoice, for the people who can pay it (owner and billing). Nothing is switched off automatically. */
function BillingBanner({ org }: { org: MeOrg }) {
  const a = org.billingAlert;
  const { pathname } = useLocation();
  if (!a || !canBilling(org.role) || pathname.endsWith("/billing")) return null;
  return (
    <div role="status" className="flex items-center justify-center gap-2 border-b border-bad-line bg-bad-bg px-5 py-2 text-[13px] text-bad sm:px-8">
      <IconAlertCircle size={15} className="shrink-0" aria-hidden="true" />
      {a.invoice ? (
        <span>
          <span className="font-semibold">An invoice for {money(a.amountDue)} was due {formatDate(a.since)}.</span> Nothing is switched off.{" "}
          <Link to={`/o/${org.slug}/billing`} className="font-semibold underline underline-offset-2">
            Pay it in Billing
          </Link>
        </span>
      ) : (
        <span>
          <span className="font-semibold">A payment of {money(a.amountDue)} didn't go through on {formatDate(a.since)}.</span> Nothing is switched off.{" "}
          <Link to={`/o/${org.slug}/billing`} className="font-semibold underline underline-offset-2">
            Update the card
          </Link>
        </span>
      )}
    </div>
  );
}

export function AppShell() {
  const { orgSlug } = useParams();
  const me = useMe();
  const org = me.data?.orgs.find((o) => o.slug === orgSlug) ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [collapsed, setCollapsed] = useLocalStorage("hx-sidebar-collapsed", false);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  const [newSite, setNewSite] = useState(false);
  const [addServer, setAddServer] = useState(false);
  const wpStash = useRef(new Map<string, WpAdmin>());

  useEffect(() => {
    if (org) {
      try {
        localStorage.setItem("hx-last-org", org.slug);
      } catch {
        /* not fatal */
      }
    }
  }, [org]);

  // A change that failed with nothing on the page saying so says it here (api/queryClient.ts).
  useEffect(() => {
    setMutationErrorFallback((message) => toast({ tone: "error", title: "That didn't work", description: message }));
    return () => setMutationErrorFallback(null);
  }, [toast]);

  // Close the mobile drawer whenever the page changes.
  useEffect(() => setDrawer(false), [location.pathname]);

  const base = `/o/${orgSlug}`;

  const onWatchedFinished = useCallback(
    (op: Operation, success: WatchMessage | null, home: WatchHome | null) => {
      // A routine job the agent was too old for isn't news.
      if (op.quiet) return;
      // Work in another org (a client's site, from the agency's Sites list; or started before the person opened this
      // org) has its log there.
      const b = home ? `/o/${home.slug}` : base;
      const where = op.siteId ? `${b}/sites/${op.siteId}` : `${b}/servers/${op.serverId}`;
      // The sheet: what came of it first, the log only for people with technical access (app/operations.tsx).
      const viewLog = { label: "See details", onClick: () => navigate(`${where}?op=${op.id}`) };
      // What came of it, when the API says (a health check's answer): that's the news, good or bad.
      if (op.outcome) {
        toast({ tone: op.outcome.tone === "good" ? "success" : "error", title: op.outcome.title, description: op.outcome.detail ?? undefined, action: viewLog });
      } else if (op.status === "succeeded" && success) {
        toast({ tone: "success", title: success.title, description: success.description });
      } else if (op.status === "succeeded") {
        toast({ tone: "success", title: op.title, description: "Finished.", action: viewLog });
      } else if (op.status === "failed") {
        toast({ tone: "error", title: op.title, description: op.error?.message ?? "The log has the details.", action: viewLog });
      } else {
        toast({ tone: "info", title: op.title, description: op.status === "expired" ? "The server didn't pick this up in time." : "This was canceled.", action: viewLog });
      }
    },
    [base, navigate, toast],
  );

  useLiveEvents(org?.id ?? "", onWatchedFinished, org?.slug);
  // Inside the app, the brand is the current org's agency: an agency's clients only ever see the agency.
  useApplyBrand(org?.id ?? null);

  const ui = useMemo<ShellUI>(
    () => ({
      openNewSite: () => setNewSite(true),
      openAddServer: () => setAddServer(true),
      openPalette: () => setPalette(true),
      openShortcuts: () => setShortcuts(true),
      stashWpAdmin: (siteId, wp) => wpStash.current.set(siteId, wp),
      takeWpAdmin: (siteId) => {
        const wp = wpStash.current.get(siteId) ?? null;
        wpStash.current.delete(siteId);
        return wp;
      },
    }),
    [],
  );

  // Global keyboard shortcuts.
  useEffect(() => {
    let pendingG = 0;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e) || e.defaultPrevented) return;
      // Leave keys alone while a dialog, sheet or menu is open.
      if (document.querySelector('[role="dialog"], [role="menu"], [role="alertdialog"]')) return;

      if (pendingG && Date.now() - pendingG < 1200) {
        pendingG = 0;
        const n = navFor(org).find((x) => x.key === e.key.toLowerCase());
        if (n) {
          e.preventDefault();
          navigate(n.to ? `${base}/${n.to}` : base);
        } else if (e.key === ",") {
          e.preventDefault();
          navigate(`${base}/settings`);
        }
        return;
      }
      if (e.key === "g") {
        pendingG = Date.now();
      } else if (e.key === "?") {
        e.preventDefault();
        setShortcuts(true);
      } else if (e.key === "n" && org && canCreateSites(org)) {
        e.preventDefault();
        setNewSite(true);
      } else if (e.key === "[") {
        e.preventDefault();
        setCollapsed(!collapsed);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [base, navigate, collapsed, setCollapsed, org]);

  if (!org) return null; // the route loader redirects before this can happen

  return (
    <OrgContext.Provider value={org}>
      <ShellContext.Provider value={ui}>
        <a
          href="#main"
          className="sr-only z-[80] rounded-md bg-surface px-4 py-2 font-semibold text-fg shadow-[var(--hx-shadow-pop)] focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
        >
          Skip to content
        </a>
        <div className="flex min-h-dvh">
          {/* Desktop sidebar */}
          <aside
            aria-label="Sidebar"
            className={cx(
              "sticky top-0 hidden h-dvh shrink-0 border-r border-line bg-sidebar transition-[width] duration-200 ease-out lg:block",
              collapsed ? "w-[68px]" : "w-[248px]",
            )}
          >
            <SidebarContent collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* The top bar: breadcrumbs, the bell, Help and you (menu and brand on a phone). */}
            <TopBar onOpenNav={() => setDrawer(true)} />

            {/* A platform admin inside an account: what they're viewing, and the way back (app/accounts.tsx). */}
            <AccountStrip />
            <LiveBanner />
            <BillingBanner org={org} />

            <main id="main" tabIndex={-1} className="flex-1 outline-none">
              <PageLayout width={pageWidth(location.pathname.replace(/^\/o\/[^/]+\/?/, ""))}>
                {/* An org that asks this session to sign in with Google first (Sign-in domains): that, in place of its pages. */}
                {org.googleRequired ? <GoogleGate org={org} /> : <Outlet />}
              </PageLayout>
            </main>
          </div>
        </div>

        {/* Mobile drawer */}
        <R.Root open={drawer} onOpenChange={setDrawer}>
          <R.Portal>
            <R.Overlay className="hx-overlay fixed inset-0 z-50 bg-[var(--hx-overlay)] lg:hidden" />
            <R.Content
              aria-describedby={undefined}
              className="hx-sheet-left fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] bg-sidebar shadow-[var(--hx-shadow-pop)] lg:hidden"
            >
              <R.Title className="sr-only">Navigation</R.Title>
              <R.Close
                aria-label="Close navigation"
                className="absolute top-4 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500"
              >
                <IconX size={17} />
              </R.Close>
              <SidebarContent onNavigate={() => setDrawer(false)} />
            </R.Content>
          </R.Portal>
        </R.Root>

        <Suspense fallback={null}>
          <CommandPalette open={palette} onOpenChange={setPalette} />
          <ShortcutsSheet open={shortcuts} onOpenChange={setShortcuts} />
          <NewSiteSheet open={newSite} onOpenChange={setNewSite} />
          <AddServerDialog open={addServer} onOpenChange={setAddServer} />
        </Suspense>
      </ShellContext.Provider>
    </OrgContext.Provider>
  );
}
